/**
 * Subtitle normalizer — PROTECTED ARTIFACT (docs/research/migration-assessment.md §5–6).
 *
 * Exact port of ~/code-scratch/georgian-video-app/server/app/subtitles.py (91 lines): import
 * normalizes caption cues into flat dialogue lines — HTML tags / BOM / zero-width chars stripped,
 * internal whitespace collapsed, "- speaker one / - speaker two" dash cues split into separate
 * lines with times interpolated by character count, zero/negative-duration cues extended by
 * 500 ms, and overlapping rolling ASR captions clamped so only one line is active at a time.
 *
 * Erasable-syntax TypeScript only (no enums, no parameter properties, no imports): the seed
 * script (packages/db/scripts/seed-m2.mjs) imports this file directly under Node's type
 * stripping, and the add-video route will use it at insert time (M3).
 *
 * Python → TS mapping:
 *   TAG_RE            → TAG_RE
 *   _clean            → clean
 *   _split_dash_cue   → splitDashCue
 *   _interpolate      → interpolate
 *   parse_srt         → parseSrt (the `srt.parse` half) + normalizeCues (the normalization half)
 *   compose_srt       → not ported (export-only feature, not needed by nelnela)
 *
 * ONE DELIBERATE DEVIATION: Python computed `int(timedelta.total_seconds() * 1000)`, which has a
 * float artifact for some values (00:08:38,800 → 518799 in Python; 7 such timestamps in the
 * georgian-asr.srt fixture). We compute h*3600000 + m*60000 + s*1000 + ms exactly, so those cues
 * land 1 ms later than the Python output. Everything downstream (split, interpolate, clamp) is
 * arithmetic on those integers and is bit-identical.
 *
 * Whitespace: Python `\s` / `str.strip()` are `str.isspace()`, which differs from JS `\s` /
 * `trim()` at the margins (Python: \x1c-\x1f and \x85 are space, ﻿ is not; JS: the
 * reverse). We use an explicit class matching Python's set so the port is exact even there.
 * Character counts for interpolation are code points (Python `len`), not UTF-16 units.
 */

/** One raw caption cue as parsed from SRT/VTT — text may still hold newlines and tags. */
export interface Cue {
  startMs: number;
  endMs: number;
  text: string;
}

/** One normalized transcript line, ready for the `line` table (speaker is '' at import). */
export interface NormalizedLine {
  startMs: number;
  endMs: number;
  speaker: string;
  text: string;
}

/** Python `TAG_RE = re.compile(r"<[^>]+>")` — `[^>]` spans newlines in both languages. */
const TAG_RE = /<[^>]+>/g;

/** Python `str.isspace()` (== Python `\s` in a str pattern) as a JS character class body. */
const PY_WS =
  '\\t\\n\\v\\f\\r\\x1c-\\x1f \\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
/** Python `re.sub(r"\s+", " ", …)`. */
const WS_RUN_RE = new RegExp(`[${PY_WS}]+`, 'g');
/** Python `str.strip()`. */
const STRIP_RE = new RegExp(`^[${PY_WS}]+|[${PY_WS}]+$`, 'g');
/** Python `re.match(r"^[-–—]\s*\S", l)` — a hyphen, en dash or em dash then a non-space. */
const DASH_LINE_RE = new RegExp(`^[-–—][${PY_WS}]*[^${PY_WS}]`);
/** Python `re.sub(r"^[-–—]\s*", "", l)`. */
const DASH_PREFIX_RE = new RegExp(`^[-–—][${PY_WS}]*`);

/** Python `s.strip()` (all Unicode whitespace per isspace(); NOT ​ / ﻿). */
function pyStrip(s: string): string {
  return s.replace(STRIP_RE, '');
}

/** Python `len(s)` — code points, not UTF-16 code units. */
function pyLen(s: string): number {
  return Array.from(s).length;
}

/**
 * Port of `_clean`: strip tags, drop zero-width space (​) and BOM (﻿), collapse every
 * whitespace run to one space, strip. Order matters: ﻿ is removed *before* the whitespace
 * collapse, so it never becomes a space.
 */
function clean(text: string): string {
  text = text.replace(TAG_RE, '');
  text = text.replaceAll('​', '').replaceAll('﻿', '');
  return pyStrip(text.replace(WS_RUN_RE, ' '));
}

/**
 * Port of `_split_dash_cue`: if a cue's lines look like '- speaker one\n- speaker two', return
 * the parts with the dash prefix removed; else []. Requires ≥2 non-empty lines and ALL of them
 * dashed — a cue where only some lines are dashed is left whole (the dashes then survive into
 * the text, as in Python).
 */
function splitDashCue(content: string): string[] {
  const rawLines = content
    .split('\n')
    .map((l) => pyStrip(l))
    .filter((l) => l !== '');
  const dashed = rawLines.filter((l) => DASH_LINE_RE.test(l));
  if (dashed.length >= 2 && dashed.length === rawLines.length) {
    return rawLines.map((l) => l.replace(DASH_PREFIX_RE, ''));
  }
  return [];
}

/**
 * Port of `_interpolate`: split [start,end] proportionally to each part's character count
 * (each part weighs max(len, 1) so empty parts still get a slot). Python `int()` on a
 * non-negative float == Math.trunc; `/` is true division == JS `/`; IEEE doubles on both sides,
 * so the spans match bit for bit. The last part ends exactly at endMs; each part starts where the
 * previous ended, so no gaps and no drift.
 */
function interpolate(startMs: number, endMs: number, parts: string[]): [number, number][] {
  const total = parts.reduce((acc, p) => acc + Math.max(pyLen(p), 1), 0);
  const spans: [number, number][] = [];
  let t = startMs;
  for (let i = 0; i < parts.length; i++) {
    const share = Math.max(pyLen(parts[i]), 1) / total;
    const dur = Math.trunc((endMs - startMs) * share);
    const end = i === parts.length - 1 ? endMs : t + dur;
    spans.push([t, end]);
    t = end;
  }
  return spans;
}

/** Python `text.lstrip("﻿")` — strips every leading BOM, not just one. */
function stripBom(text: string): string {
  return text.replace(/^﻿+/, '');
}

/**
 * The port of subtitles.py `parse_srt`'s normalization, applied to already-parsed cues (the
 * body of the `for sub in srt.parse(text)` loop, then the sort and the overlap clamp).
 * `cue.text` is the raw multi-line content: it may still hold newlines and tags.
 */
export function normalizeCues(cues: Cue[]): NormalizedLine[] {
  const out: NormalizedLine[] = [];
  for (const cue of cues) {
    const startMs = cue.startMs;
    let endMs = cue.endMs;
    // Zero/negative duration → 500 ms, decided on the raw times BEFORE tag strip / split.
    if (endMs <= startMs) endMs = startMs + 500;
    // Tags are stripped from the whole multi-line content before the dash split, so a tag
    // spanning a line break can't hide a dash prefix (or fabricate one).
    const content = cue.text.replace(TAG_RE, '');
    const parts = splitDashCue(content);
    if (parts.length > 0) {
      const cleaned = parts.map(clean);
      const spans = interpolate(startMs, endMs, cleaned);
      for (let i = 0; i < cleaned.length; i++) {
        const part = cleaned[i];
        // Empty parts still took a share of the time in interpolate() (Python did the same);
        // they are only dropped here.
        if (part) out.push({ startMs: spans[i][0], endMs: spans[i][1], speaker: '', text: part });
      }
    } else {
      const text = clean(content);
      if (text) out.push({ startMs, endMs, speaker: '', text });
    }
  }
  // Python list.sort is stable; JS Array.prototype.sort is stable since ES2019 — ties keep
  // file order (matters for split parts and identical-start ASR cues).
  out.sort((a, b) => a.startMs - b.startMs);
  // Auto-generated (rolling) captions overlap each other: cue N+1 starts before cue N ends.
  // Clamp so only one line is "active" at a time in the transcript. Sequential in-place
  // mutation, exactly as Python's `for a, b in zip(out, out[1:])`: a's new end never feeds
  // back into a later comparison (only b's end does, and b's end is untouched here).
  for (let i = 0; i + 1 < out.length; i++) {
    const a = out[i];
    const b = out[i + 1];
    if (a.endMs > b.startMs) a.endMs = Math.max(b.startMs, a.startMs + 200);
  }
  return out;
}

/** 'HH:MM:SS,mmm --> HH:MM:SS,mmm' — comma or dot; anything after the end time is ignored. */
const SRT_TIMING_RE = /^[ \t]*(\d+):(\d+):(\d+)[,.](\d+)[ \t]*-->[ \t]*(\d+):(\d+):(\d+)[,.](\d+)/;

/**
 * Exact integer arithmetic (the documented deviation from Python's float round trip). The ms
 * field is taken literally like Python's `int(msecs)` — ',5' is 5 ms, not 500.
 */
function toMs(h: string, m: string, s: string, ms: string): number {
  return Number(h) * 3600000 + Number(m) * 60000 + Number(s) * 1000 + Number(ms);
}

/**
 * Minimal SRT reader: index line optional and ignored, timing line as above, content = the lines
 * up to the next blank line (or EOF), CRLF tolerated, leading BOM(s) stripped. Cues with empty
 * content are still yielded (normalizeCues drops them). Deliberately NOT a re-implementation of
 * the Python `srt` library's tolerances (colon ms delimiters, `- >` arrows, blank lines inside
 * content, …). Lines that don't start a cue are skipped.
 */
export function parseSrt(text: string): Cue[] {
  const lines = stripBom(text).replace(/\r\n?/g, '\n').split('\n');
  const cues: Cue[] = [];
  let i = 0;
  while (i < lines.length) {
    let m = SRT_TIMING_RE.exec(lines[i]);
    if (!m && i + 1 < lines.length) {
      // Any single non-timing line directly before a timing line is the (ignored) index.
      const next = SRT_TIMING_RE.exec(lines[i + 1]);
      if (next) {
        m = next;
        i++;
      }
    }
    if (!m) {
      i++;
      continue;
    }
    i++;
    const content: string[] = [];
    while (i < lines.length && lines[i] !== '') {
      content.push(lines[i]);
      i++;
    }
    cues.push({
      startMs: toMs(m[1], m[2], m[3], m[4]),
      endMs: toMs(m[5], m[6], m[7], m[8]),
      text: content.join('\n'),
    });
  }
  return cues;
}

/** '[HH:]MM:SS.mmm --> [HH:]MM:SS.mmm' plus optional cue settings (ignored). Comma tolerated. */
const VTT_TIMING_RE =
  /^[ \t]*(?:(\d+):)?(\d+):(\d+)[.,](\d+)[ \t]*-->[ \t]*(?:(\d+):)?(\d+):(\d+)[.,](\d+)/;

/**
 * Minimal WebVTT reader: the header block (WEBVTT line + anything up to the first blank line) is
 * skipped, as are NOTE / STYLE / REGION blocks and any other block without a timing line; a cue
 * is an optional identifier line, the timing line, and content up to the next blank line.
 * Voice/class tags (<v Speaker>, <c.x>, <00:01.000> karaoke stamps) are left raw in Cue.text —
 * normalizeCues strips them via TAG_RE. Tolerance beyond the spec: a timing line inside the
 * header block ends the header early (files that forget the blank line after WEBVTT).
 */
export function parseVtt(text: string): Cue[] {
  const lines = stripBom(text).replace(/\r\n?/g, '\n').split('\n');
  const cues: Cue[] = [];
  let i = 0;
  // Header block.
  while (i < lines.length && lines[i] !== '' && !VTT_TIMING_RE.test(lines[i])) i++;
  while (i < lines.length) {
    if (lines[i] === '') {
      i++;
      continue;
    }
    let m = VTT_TIMING_RE.exec(lines[i]);
    if (!m && i + 1 < lines.length) {
      const next = VTT_TIMING_RE.exec(lines[i + 1]);
      if (next) {
        // lines[i] is the cue identifier — ignored.
        m = next;
        i++;
      }
    }
    if (!m) {
      // NOTE / STYLE / REGION / unknown block: skip through the next blank line.
      while (i < lines.length && lines[i] !== '') i++;
      continue;
    }
    i++;
    const content: string[] = [];
    while (i < lines.length && lines[i] !== '') {
      content.push(lines[i]);
      i++;
    }
    cues.push({
      startMs: toMs(m[1] ?? '0', m[2], m[3], m[4]),
      endMs: toMs(m[5] ?? '0', m[6], m[7], m[8]),
      text: content.join('\n'),
    });
  }
  return cues;
}

/** Sniffs the format (WEBVTT header → VTT, else SRT). */
export function parseCaptions(text: string): Cue[] {
  const body = stripBom(text);
  return body.startsWith('WEBVTT') ? parseVtt(body) : parseSrt(body);
}
